import { useState } from "react";
import { Button, Tag, message } from "antd";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import { membershipRetailPurchaseState } from "../../../products/membership/membershipPurchaseUtils";
import { membershipNoRetailPurchaseHint } from "../spacePageUtils";
import { SpaceProductPurchaseModal } from "./SpaceProductPurchaseModal";
import { SpaceProductUpgradeModal } from "./SpaceProductUpgradeModal";

export function SpaceHomeMembershipSection(props: {
  spaceId: string;
  row: MembershipProduct;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  spaceProfile: SpaceProfile | null;
  subscribeCtaLabel?: string;
  subscribePrimaryButtonClassName?: string;
  subscribeCtaPresentation?: "button" | "textLink";
  /** 外部接管订阅主按钮点击（如套餐选择弹框） */
  onSubscribePrimaryClick?: () => void;
}): JSX.Element {
  const {
    spaceId,
    row,
    isLoggedIn,
    onLoginRequest,
    spaceProfile,
    subscribeCtaLabel,
    subscribePrimaryButtonClassName,
    subscribeCtaPresentation = "button",
    onSubscribePrimaryClick,
  } = props;
  const retailCta = subscribeCtaLabel?.trim() || "订阅";
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const { isMarket, canBuyMore, canBuyRetail } = membershipRetailPurchaseState(row);
  const upgradeEligible =
    row.upgradeEligible === true && !!row.upgradeSourceSpaceProductId?.trim();

  const openUpgradeModal = () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    setUpgradeOpen(true);
  };

  const openPurchaseModal = () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (onSubscribePrimaryClick) {
      onSubscribePrimaryClick();
      return;
    }
    if (row.purchaseBlocked) {
      message.info(membershipNoRetailPurchaseHint(row));
      return;
    }
    if (!row.userSubscribed && !canBuyRetail) {
      message.info(membershipNoRetailPurchaseHint(row));
      return;
    }
    if (row.userSubscribed && !canBuyMore) {
      message.info("暂无可续订规格");
      return;
    }
    setPurchaseOpen(true);
  };

  const upgradeModal =
    onSubscribePrimaryClick ? null : (
      <SpaceProductUpgradeModal
        spaceId={spaceId}
        row={row}
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        isLoggedIn={isLoggedIn}
        onLoginRequest={onLoginRequest}
        spaceProfile={spaceProfile}
      />
    );

  const purchaseModal =
    onSubscribePrimaryClick ? null : (
    <SpaceProductPurchaseModal
      spaceId={spaceId}
      row={row}
      open={purchaseOpen}
      onClose={() => setPurchaseOpen(false)}
      isLoggedIn={isLoggedIn}
      onLoginRequest={onLoginRequest}
      spaceProfile={spaceProfile}
    />
  );

  if (row.userSubscribed) {
    if (isMarket) {
      const n =
        row.subscribedStrategyCount ??
        row.strategyEntitlements?.filter((e) => e.active).length ??
        0;
      return (
        <>
          <div className="space-m-purchase-col">
            <div className="space-m-actions space-m-actions--subscribed">
              <Tag className="space-m-tag space-m-tag--active">已开通</Tag>
              <span className="space-m-days">已订阅{n}个策略</span>
              {canBuyMore ? (
                <Button
                  type="primary"
                  className="space-m-btn space-m-btn--pill space-m-btn--renew"
                  onClick={openPurchaseModal}
                >
                  订阅更多
                </Button>
              ) : null}
            </div>
          </div>
          {purchaseModal}
        </>
      );
    }
    const d = row.daysRemaining;
    return (
      <>
        <div className="space-m-purchase-col">
          <div className="space-m-actions space-m-actions--subscribed">
            <Tag className="space-m-tag space-m-tag--active">已开通</Tag>
            {d != null && d >= 0 ? (
              <span className="space-m-days">剩余{d}天</span>
            ) : null}
            {canBuyMore ? (
              <Button
                type="primary"
                className="space-m-btn space-m-btn--pill space-m-btn--renew"
                onClick={openPurchaseModal}
              >
                续订
              </Button>
            ) : null}
          </div>
        </div>
        {purchaseModal}
      </>
    );
  }

  return (
    <>
      <div
        className={
          subscribeCtaPresentation === "textLink"
            ? "space-m-purchase-col space-m-purchase-col--inline"
            : "space-m-purchase-col"
        }
      >
        {upgradeEligible ? (
          subscribeCtaPresentation === "textLink" ? (
            <button type="button" className="space-m-subscribe-text" onClick={openUpgradeModal}>
              升级
            </button>
          ) : (
            <Button
              type="primary"
              className={
                subscribePrimaryButtonClassName ??
                "space-m-btn space-m-btn--pill space-m-btn--subscribe space-m-btn--upgrade"
              }
              onClick={openUpgradeModal}
            >
              升级
            </Button>
          )
        ) : canBuyRetail ? (
          subscribeCtaPresentation === "textLink" ? (
            <button
              type="button"
              className="space-m-subscribe-text"
              onClick={openPurchaseModal}
            >
              {retailCta}
            </button>
          ) : (
            <Button
              type="primary"
              className={
                subscribePrimaryButtonClassName ??
                "space-m-btn space-m-btn--pill space-m-btn--subscribe"
              }
              onClick={openPurchaseModal}
            >
              {retailCta}
            </Button>
          )
        ) : (
          <span className="space-m-no-spec-hint">{membershipNoRetailPurchaseHint(row)}</span>
        )}
      </div>
      {upgradeModal}
      {purchaseModal}
    </>
  );
}
